import { useEffect, useState, useRef } from "react"
import { DatePicker } from "@mui/x-date-pickers"
import { Toast } from "primereact/toast"
import dayjs from "dayjs"
import { CalendarDays, ChevronLeft, ChevronRight, X, ChevronDown, Search } from "lucide-react"
import axios from "axios"
import { ClimbingBoxLoader } from "react-spinners"
import API_BASE_URL from "../../Config"

const Repository = () => {
  const toast = useRef(null)
  const [formData, setFormData] = useState({
    companyName: "",
    siteCode: [],
    projectName: [],
    state: [],
    location: [],
    specificRecord: "",
  })

  const [dropdownOptions, setDropdownOptions] = useState({
    company_names: [],
    site_codes: [],
    project_names: [],
    states: [],
    locations: [],
    specific_record: [],
  })

  // Store original options that never get filtered
  // eslint-disable-next-line no-unused-vars
  const [originalDropdownOptions, setOriginalDropdownOptions] = useState({
    company_names: [],
    site_codes: [],
    project_names: [],
    states: [],
    locations: [],
    specific_record: [],
  })

  // Add cascading filter state
  const [cascadingOptions, setCascadingOptions] = useState({
    company_names: [],
    site_codes: [],
    project_names: [],
    states: [],
    locations: [],
    specific_record: [],
  })

  // Add filtered data state to store the current filtered dataset
  // Add this new state after the existing state declarations
  const [originalDataset, setOriginalDataset] = useState([])

  const [openDropdown, setOpenDropdown] = useState(null)

  // Search terms for each dropdown
  const [searchTerms, setSearchTerms] = useState({
    companyName: "",
    siteCode: "",
    projectName: "",
    state: "",
    location: "",
    specificRecord: "",
  })

  const [fromMonth, setFromMonth] = useState(null)
  const [toMonth, setToMonth] = useState(null)
  const [tableData, setTableData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isAnyDropdownOpen, setIsAnyDropdownOpen] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [apiClientId, setApiClientId] = useState(null)
  const [filtersApplied, setFiltersApplied] = useState(false)

  const client_id = sessionStorage.getItem("client_id")
  const role_id = sessionStorage.getItem("user_role_id")
  const token = sessionStorage.getItem("token")
  const username = sessionStorage.getItem("current_username") || sessionStorage.getItem("current_username")

  const isRoleOne = role_id === "1" || role_id === 1

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const rowsPerPage = 12
  const paginatedData = tableData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
  const totalPages = Math.ceil(tableData.length / rowsPerPage)

  // Toast helper functions
  const showSuccess = (summary, detail, options) => {
    toast.current?.show({
      severity: "success",
      summary: summary,
      detail: detail,
      life: 0,
      closable: true,
      sticky: true,
      ...options,
    })
  }

  const showError = (summary, detail, options) => {
    toast.current?.show({
      severity: "error",
      summary: summary,
      detail: detail,
      life: 0,
      closable: true,
      sticky: true,
      ...options,
    })
  }

  const showWarn = (summary, detail) => {
    toast.current?.show({
      severity: "warn",
      summary: summary,
      detail: detail,
      life: 0,
      closable: true,
      sticky: true,
    })
  }

  // Helper function to handle API errors
  const handleApiError = (error, defaultMessage = "SOMETHING WENT WRONG!!!") => {
    if (error.response) {
      const status = error.response.status
      if (status === 500) {
        return "SOMETHING WENT WRONG!!!"
      }
      const errorMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        error.response.data?.detail ||
        error.response.statusText ||
        defaultMessage
      return errorMessage
    } else if (error.request) {
      return "Network error. Please check your connection and try again."
    } else {
      return error.message || defaultMessage
    }
  }

  // Helper function to get value regardless of case sensitivity
  const getFieldValue = (item, fieldOptions) => {
    for (const field of fieldOptions) {
      if (item[field] !== undefined) {
        return item[field]
      }
      const fieldLower = field.toLowerCase()
      for (const key of Object.keys(item)) {
        if (key.toLowerCase() === fieldLower) {
          return item[key]
        }
      }
    }
    return null
  }

  // Helper function to extract and format date from row data
  const getRowDate = (row) => {
    const dateValue = getFieldValue(row, [
      "date",
      "Date",
      "DATE",
      "period",
      "Period",
      "PERIOD",
      "month_year",
      "Month_Year",
      "monthYear",
      "MonthYear",
      "reporting_date",
      "Reporting_Date",
      "report_date",
      "Report_Date",
      "created_date",
      "Created_Date",
      "updated_date",
      "Updated_Date",
    ])

    if (dateValue) {
      try {
        const parsedDate = dayjs(dateValue)
        if (parsedDate.isValid()) {
          return parsedDate.format("MMM YYYY")
        }
      } catch (error) {
        // Date parsing error - use fallback
      }
    }
    return dayjs().format("MMM YYYY")
  }

  // Helper function to extract specific field values from row data
  const getTableFieldValue = (row, fieldType) => {
    switch (fieldType) {
      case "project_code":
        return getFieldValue(row, [
          "project_code",
          "Project_code",
          "site_code",
          "Site_code",
          "projectcode",
          "ProjectCode",
        ])
      case "project_name":
        return getFieldValue(row, ["project_name", "Project_name", "Project_Name", "projectname", "ProjectName"])
      case "state":
        return getFieldValue(row, ["state", "State", "STATE"])
      case "location":
        return getFieldValue(row, ["location", "Location", "site_location", "Site_Location", "LOCATION"])
      case "company_name":
        return getFieldValue(row, ["company_name", "Company_name", "Company_Name", "companyname", "CompanyName"])
      default:
        return ""
    }
  }

  // Helper function to format project names for display (replace _ with ,)
  const formatProjectNameForDisplay = (projectName) => {
    if (!projectName) return projectName
    return projectName.replace(/_/g, ",")
  }

  // ZIP File Icon Component
  const ZipIcon = ({ className = "w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="rgba(255, 165, 0, 0.2)"
      />
      <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="8" y="10" width="8" height="2" fill="currentColor" />
      <rect x="10" y="12" width="4" height="2" fill="currentColor" />
      <rect x="8" y="14" width="8" height="2" fill="currentColor" />
      <rect x="10" y="16" width="4" height="2" fill="currentColor" />
      <text x="12" y="19" textAnchor="middle" fontSize="6" fill="currentColor" fontWeight="bold">
        ZIP
      </text>
    </svg>
  )

  // Add this after the other useRef declarations
  const searchInputRef = useRef(null)
  const dropdownScrollRef = useRef(null)
  const scrollPositions = useRef({})

  // Handle single ZIP file download
  const handleZipDownload = async (row) => {
    try {
      setIsLoading(true)
      const rowId = row.id || row.project_id || row.project_code || row.site_code || row.Project_code

      if (!rowId) {
        showError("Download Failed", "No valid ID found for this row.")
        setIsLoading(false)
        return
      }

      if (!formData.specificRecord) {
        showError("Download Failed", "Please select a Specific Record to determine file type.")
        setIsLoading(false)
        return
      }

      const requestData = {
        id: String(rowId),
        file_type: "zip",
        specific_record: formData.specificRecord,
      }

      if (isRoleOne) {
        if (apiClientId) {
          requestData.client_id = apiClientId
        } else {
          showError("Download Failed", "Client ID not available for superadmin. Please apply filters first.")
          setIsLoading(false)
          return
        }
      } else if (client_id) {
        requestData.client_id = client_id
      }

      const response = await axios.post(`${API_BASE_URL}api/download_pdf_file/`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        responseType: "blob",
      })

      const blob = new Blob([response.data], { type: "application/zip" })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = downloadUrl
      // Check if company name contains CIEL (case-insensitive)
      const companyName = getTableFieldValue(row, "company_name") || ""
      const containsCiel =
        companyName.toLowerCase().includes("ciel") || companyName.toLowerCase().includes("gg instruments")

      // Use project_name if company contains CIEL & GG Instruments, otherwise use project_code
      const filePrefix = containsCiel
        ? getTableFieldValue(row, "project_name")
        : getTableFieldValue(row, "project_code")

      link.download = `${filePrefix}_${formData.specificRecord}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)

      showSuccess("Download Completed", "ZIP file has been downloaded successfully.", {
        life: 3000,
        sticky: false,
      })
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      const errorMessage = handleApiError(error, "Failed to download ZIP file.")
      showError("Download Failed", errorMessage)
    }
  }

  // Handle Download All ZIP files
  const handleDownloadAll = async () => {
    try {
      setIsLoading(true)

      if (!formData.specificRecord) {
        showError("Download Failed", "Please select a Specific Record to determine file type.")
        setIsLoading(false)
        return
      }

      const rowIds = tableData
        .map((row) => row.id || row.project_id || row.project_code || row.site_code || row.Project_code)
        .filter(Boolean)

      if (rowIds.length === 0) {
        showError("Download Failed", "No valid records found for bulk download.")
        setIsLoading(false)
        return
      }

      const requestData = {
        id: rowIds.join(","),
        file_type: "all",
        specific_record: formData.specificRecord,
      }

      if (isRoleOne) {
        if (apiClientId) {
          requestData.client_id = apiClientId
        } else {
          showError("Download Failed", "Client ID not available for superadmin. Please apply filters first.")
          setIsLoading(false)
          return
        }
      } else if (client_id) {
        requestData.client_id = client_id
      }

      const response = await axios.post(`${API_BASE_URL}api/download_pdf_file/`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        responseType: "blob",
      })

      const blob = new Blob([response.data], { type: "application/zip" })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = downloadUrl
      link.download = `bulk_${formData.specificRecord}_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)

      showSuccess(
        "Bulk Download Completed",
        `Successfully downloaded ${rowIds.length} ${formData.specificRecord} files.`,
        {
          life: 3000,
          sticky: false,
        },
      )
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      const errorMessage = handleApiError(error, "Failed to download ZIP files.")
      showError("Download Failed", errorMessage)
    }
  }

  // Replace the existing handleSearchTermChange function with this improved version
  const handleSearchTermChange = (field, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Maintain focus on the search input after state update
    setTimeout(() => {
      if (searchInputRef.current && openDropdown === field) {
        searchInputRef.current.focus()
      }
    }, 0)
  }

  const handleRemoveOption = (field, option) => {
    // Reset filtersApplied immediately when any selection changes
    if (filtersApplied) {
      setFiltersApplied(false)
    }

    let updatedFormData
    if (field === "specificRecord" || field === "companyName") {
      updatedFormData = { ...formData, [field]: "" }
      setFormData(updatedFormData)
    } else {
      const currentValues = [...formData[field]]
      const index = currentValues.indexOf(option)
      if (index !== -1) {
        currentValues.splice(index, 1)
        updatedFormData = { ...formData, [field]: currentValues }
        setFormData(updatedFormData)
      }
    }

    // Apply cascading filters after removal
    if (updatedFormData) {
      applyCascadingFilters(updatedFormData)
    }
  }

  // Handle select all for dropdown
  const handleSelectAllDropdown = (field) => {
    if (field === "specificRecord" || field === "companyName") {
      return
    }

    // Reset filtersApplied immediately when any selection changes
    if (filtersApplied) {
      setFiltersApplied(false)
    }

    // Use the same logic as in CustomDropdown to get effective options
    const effectiveOptions =
      cascadingOptions[
        field === "siteCode"
          ? "site_codes"
          : field === "projectName"
            ? "project_names"
            : field === "state"
              ? "states"
              : field === "location"
                ? "locations"
                : "site_codes"
      ].length > 0
        ? cascadingOptions[
            field === "siteCode"
              ? "site_codes"
              : field === "projectName"
                ? "project_names"
                : field === "state"
                  ? "states"
                  : field === "location"
                    ? "locations"
                    : "site_codes"
          ]
        : originalDropdownOptions[
            field === "siteCode"
              ? "site_codes"
              : field === "projectName"
                ? "project_names"
                : field === "state"
                  ? "states"
                  : field === "location"
                    ? "locations"
                    : "site_codes"
          ]

    // Apply search filter if there's a search term
    const searchTerm = searchTerms[field]
    const filteredOptions = searchTerm
      ? effectiveOptions.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()))
      : effectiveOptions

    const updatedFormData = {
      ...formData,
      [field]: formData[field].length === filteredOptions.length ? [] : [...filteredOptions],
    }
    setFormData(updatedFormData)

    // Apply cascading filters after selection
    applyCascadingFilters(updatedFormData)
  }

  // Enhanced Handle option selection with cascading filters
  const handleOptionSelect = (field, option) => {
    // Store current scroll position before state update
    const currentScrollTop = dropdownScrollRef.current?.scrollTop || 0

    // Reset filtersApplied immediately when any selection changes
    if (filtersApplied) {
      setFiltersApplied(false)
    }

    if (field === "specificRecord" || field === "companyName") {
      const updatedFormData = { ...formData, [field]: option }
      setFormData(updatedFormData)
      setOpenDropdown(null)
      setIsAnyDropdownOpen(false)
      // Clear search term for single select fields
      setSearchTerms((prev) => ({ ...prev, [field]: "" }))

      // Apply cascading filters for company name
      if (field === "companyName") {
        applyCascadingFilters(updatedFormData)
      }
    } else {
      const currentValues = [...formData[field]]
      const index = currentValues.indexOf(option)

      if (index === -1) {
        currentValues.push(option)
      } else {
        currentValues.splice(index, 1)
      }

      const updatedFormData = { ...formData, [field]: currentValues }
      setFormData(updatedFormData)

      // Restore scroll position after state update
      setTimeout(() => {
        if (dropdownScrollRef.current) {
          dropdownScrollRef.current.scrollTop = currentScrollTop
        }
      }, 0)

      // Apply cascading filters for multi-select fields
      applyCascadingFilters(updatedFormData)
    }
  }

  // CORRECTED: Function to apply cascading filters - now project names, states, and locations all depend on site code
  const applyCascadingFilters = (currentFormData) => {
    // Use the original dataset from API for filtering
    if (originalDataset.length === 0) {
      console.log("No original dataset available for cascading filters")
      return
    }

    console.log("Applying cascading filters with:", currentFormData)
    console.log("Original dataset length:", originalDataset.length)

    // Filter based on company name (for role 1)
    if (isRoleOne && currentFormData.companyName) {
      console.log("Filtering by company name:", currentFormData.companyName)
      const companyFilteredData = originalDataset.filter((item) => {
        const itemCompanyName = getFieldValue(item, [
          "company_name",
          "Company_name",
          "Company_Name",
          "companyname",
          "CompanyName",
        ])
        return itemCompanyName === currentFormData.companyName
      })

      console.log("Company filtered data:", companyFilteredData.length, "records")

      // Update site codes based on selected company
      const filteredSiteCodes = [
        ...new Set(
          companyFilteredData.map((item) =>
            getFieldValue(item, [
              "project_code",
              "Project_code",
              "site_code",
              "Site_code",
              "projectcode",
              "ProjectCode",
            ]),
          ),
        ),
      ].filter(Boolean)

      console.log("Filtered site codes:", filteredSiteCodes)

      setCascadingOptions((prev) => ({
        ...prev,
        site_codes: filteredSiteCodes,
      }))

      // Clear dependent fields when company changes
      if (formData.companyName !== currentFormData.companyName) {
        setFormData((prevData) => ({
          ...prevData,
          siteCode: [],
          projectName: [],
          state: [],
          location: [],
        }))
      }
    }

    // CORRECTED: Filter based on site code selection - now affects project names, states, AND locations
    if (currentFormData.siteCode.length > 0) {
      console.log("Filtering by site codes:", currentFormData.siteCode)
      let baseData = originalDataset

      // If company is selected, start with company-filtered data
      if (isRoleOne && currentFormData.companyName) {
        baseData = originalDataset.filter((item) => {
          const itemCompanyName = getFieldValue(item, [
            "company_name",
            "Company_name",
            "Company_Name",
            "companyname",
            "CompanyName",
          ])
          return itemCompanyName === currentFormData.companyName
        })
      }

      const siteFilteredData = baseData.filter((item) => {
        const itemSiteCode = getFieldValue(item, [
          "project_code",
          "Project_code",
          "site_code",
          "Site_code",
          "projectcode",
          "ProjectCode",
        ])
        return currentFormData.siteCode.includes(itemSiteCode)
      })

      console.log("Site filtered data:", siteFilteredData.length, "records")

      // Update project names based on selected site codes
      const filteredProjectNames = [
        ...new Set(
          siteFilteredData.map((item) =>
            getFieldValue(item, ["project_name", "Project_name", "Project_Name", "projectname", "ProjectName"]),
          ),
        ),
      ].filter(Boolean)

      // CORRECTED: Also update states and locations based on selected site codes
      const filteredStates = [
        ...new Set(siteFilteredData.map((item) => getFieldValue(item, ["state", "State", "STATE"]))),
      ].filter(Boolean)

      const filteredLocations = [
        ...new Set(
          siteFilteredData.map((item) =>
            getFieldValue(item, ["location", "Location", "site_location", "Site_Location", "LOCATION"]),
          ),
        ),
      ].filter(Boolean)

      console.log("Filtered project names:", filteredProjectNames)
      console.log("Filtered states:", filteredStates)
      console.log("Filtered locations:", filteredLocations)

      setCascadingOptions((prev) => ({
        ...prev,
        project_names: filteredProjectNames,
        states: filteredStates,
        locations: filteredLocations,
      }))

      // Clear dependent fields when site code changes and filter existing selections
      const previousSiteCodes = formData.siteCode
      if (JSON.stringify(previousSiteCodes.sort()) !== JSON.stringify(currentFormData.siteCode.sort())) {
        setFormData((prevData) => ({
          ...prevData,
          projectName: prevData.projectName.filter((name) => filteredProjectNames.includes(name)),
          state: prevData.state.filter((state) => filteredStates.includes(state)),
          location: prevData.location.filter((location) => filteredLocations.includes(location)),
        }))
      }
    } else if (isRoleOne && currentFormData.companyName) {
      // Reset to company-filtered options if no site code selected but company is selected
      const companyFilteredData = originalDataset.filter((item) => {
        const itemCompanyName = getFieldValue(item, [
          "company_name",
          "Company_name",
          "Company_Name",
          "companyname",
          "CompanyName",
        ])
        return itemCompanyName === currentFormData.companyName
      })

      const companyProjectNames = [
        ...new Set(
          companyFilteredData.map((item) =>
            getFieldValue(item, ["project_name", "Project_name", "Project_Name", "projectname", "ProjectName"]),
          ),
        ),
      ].filter(Boolean)

      const companyStates = [
        ...new Set(companyFilteredData.map((item) => getFieldValue(item, ["state", "State", "STATE"]))),
      ].filter(Boolean)

      const companyLocations = [
        ...new Set(
          companyFilteredData.map((item) =>
            getFieldValue(item, ["location", "Location", "site_location", "Site_Location", "LOCATION"]),
          ),
        ),
      ].filter(Boolean)

      setCascadingOptions((prev) => ({
        ...prev,
        project_names: companyProjectNames,
        states: companyStates,
        locations: companyLocations,
      }))
    } else {
      // Reset to original options if no filters
      setCascadingOptions((prev) => ({
        ...prev,
        project_names: originalDropdownOptions.project_names,
        states: originalDropdownOptions.states,
        locations: originalDropdownOptions.locations,
      }))
    }

    // Note: Removed the separate project name and state filtering logic since
    // project names, states, and locations now all depend directly on site code selection
  }

  // Handle Apply function with validation
  const handleApply = async () => {
    if (!formData.specificRecord) {
      showError("Validation Error", "Please select a Specific Record before applying filters.", {
        life: 3000,
        sticky: false,
      })
      return
    }

    setIsLoading(true)
    try {
      const usernameFromStorage =
        sessionStorage.getItem("current_username") || sessionStorage.getItem("current_username") || ""

      const requestData = {
        role_id: role_id,
      }

      if (!isRoleOne && client_id) {
        requestData.client_id = client_id
      }

      if (role_id === "3" || role_id === 3) {
        requestData.username = usernameFromStorage
      }

      if (formData.siteCode.length > 0) {
        requestData.project_code = formData.siteCode.join(",")
      }

      if (formData.projectName.length > 0) {
        requestData.project_name = formData.projectName.join(",")
      }

      if (formData.location.length > 0) {
        requestData.location = formData.location.join(",")
      }

      if (formData.state.length > 0) {
        requestData.state = formData.state.join(",")
      }

      // Updated to handle single select company name
      if (isRoleOne && formData.companyName) {
        requestData.company_name = formData.companyName
      }

      if (formData.specificRecord) {
        requestData.specific_record = formData.specificRecord
      }

      if (fromMonth) {
        requestData.from_date = dayjs(fromMonth).format("YYYY-MM-DD")
      }

      if (toMonth) {
        requestData.to_date = dayjs(toMonth).format("YYYY-MM-DD")
      }

      const res = await axios.post(`${API_BASE_URL}api/repository_details/`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      let responseData = []
      if (res.data && Array.isArray(res.data)) {
        responseData = res.data
      } else if (res.data && res.data.data && Array.isArray(res.data.data)) {
        responseData = res.data.data
      } else if (res.data && res.data.results && Array.isArray(res.data.results)) {
        responseData = res.data.results
      }

      // Extract client_id from the first record for role_id 1
      if (isRoleOne && responseData[0]) {
        if (responseData[0].client_id) {
          setApiClientId(responseData[0].client_id)
        } else {
          showWarn("Client ID Missing", "No client_id found in the response. Downloads may not work properly.")
        }
      }

      if (responseData.length > 0) {
        setTableData(responseData)
        setCurrentPage(1)
        setFiltersApplied(true)
      } else {
        showWarn("No Data Found", "No data returned from server. Please try different filter criteria.")
        setTableData([])
        setFiltersApplied(false)
      }
    } catch (err) {
      const errorMessage = handleApiError(err, "Failed to load repository data.")
      showError("API Error", errorMessage)
      setFiltersApplied(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClear = () => {
    const clearedFormData = {
      companyName: "", // Changed to empty string for single select
      siteCode: [],
      projectName: [],
      state: [],
      location: [],
      specificRecord: "",
    }
    setFormData(clearedFormData)
    setFromMonth(null)
    setToMonth(null)
    setTableData([])
    setCurrentPage(1)
    setApiClientId(null)
    setFiltersApplied(false)
    setSearchTerms({
      companyName: "",
      siteCode: "",
      projectName: "",
      state: "",
      location: "",
      specificRecord: "",
    })

    // Reset dropdown options to original
    setDropdownOptions(originalDropdownOptions)
    setCascadingOptions(originalDropdownOptions)
  }

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }

  const goToPage = (page) => {
    setCurrentPage(page)
  }

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = window.innerWidth < 640 ? 3 : window.innerWidth < 1024 ? 4 : 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return pages
  }

  useEffect(() => {
    if (!isRoleOne && !client_id) {
      showError("Configuration Error", "Client ID is missing. Please login again.")
      return
    }

    if (!role_id) {
      showError("Configuration Error", "Role ID is missing. Please login again.")
      return
    }

    if (!token) {
      showError("Configuration Error", "Authentication token is missing. Please login again.")
      return
    }

    if (!username) {
      showError("Configuration Error", "Username is missing. Please login again.")
      return
    }

    const requestData = {
      role_id,
      username,
    }

    if (!isRoleOne && client_id) {
      requestData.client_id = client_id
    }

    if (isRoleOne) {
      requestData.include_company_names = true
    }

    axios
      .post(`${API_BASE_URL}api/filter_data/`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const dataArray = res.data.data || []
        // Store the original dataset for cascading filters
        setOriginalDataset(dataArray)
        const directSpecificRecord = res.data.specific_record || []

        if (dataArray.length === 0) {
          showWarn("No Data", "No data available for your role. Please contact administrator.")
          return
        }

        // Extract field names from the first record
        const firstRecord = dataArray[0] || {}
        const siteCodeField = Object.keys(firstRecord).find(
          (key) =>
            key.toLowerCase().includes("project_code") ||
            key.toLowerCase().includes("projectcode") ||
            key.toLowerCase().includes("site_code") ||
            key.toLowerCase().includes("sitecode"),
        )

        const projectNameField = Object.keys(firstRecord).find(
          (key) => key.toLowerCase().includes("project_name") || key.toLowerCase().includes("projectname"),
        )

        const stateField = Object.keys(firstRecord).find((key) => key.toLowerCase().includes("state"))

        const locationField = Object.keys(firstRecord).find(
          (key) => key.toLowerCase().includes("location") || key.toLowerCase().includes("site_location"),
        )

        const companyNameField = isRoleOne
          ? Object.keys(firstRecord).find(
              (key) => key.toLowerCase().includes("company_name") || key.toLowerCase().includes("companyname"),
            )
          : null

        const site_codes = [
          ...new Set(
            dataArray.map((item) =>
              getFieldValue(item, [siteCodeField, "project_code", "Project_code", "site_code", "Site_code"]),
            ),
          ),
        ].filter(Boolean)

        const project_names = [
          ...new Set(
            dataArray.map((item) =>
              getFieldValue(item, [projectNameField, "project_name", "Project_Name", "Project_name"]),
            ),
          ),
        ].filter(Boolean)

        const states = [
          ...new Set(dataArray.map((item) => getFieldValue(item, [stateField, "state", "State"]))),
        ].filter(Boolean)

        const locations = [
          ...new Set(
            dataArray.map((item) =>
              getFieldValue(item, [locationField, "location", "Location", "site_location", "Site_Location"]),
            ),
          ),
        ].filter(Boolean)

        const company_names = isRoleOne
          ? [
              ...new Set(
                dataArray.map((item) =>
                  getFieldValue(item, [companyNameField, "company_name", "Company_Name", "Company_name"]),
                ),
              ),
            ].filter(Boolean)
          : []

        const specific_record = directSpecificRecord.length > 0 ? directSpecificRecord : []

        const finalDropdownOptions = {
          company_names,
          site_codes,
          project_names,
          states,
          locations,
          specific_record,
        }

        setDropdownOptions(finalDropdownOptions)
        setOriginalDropdownOptions(finalDropdownOptions)
        setCascadingOptions(finalDropdownOptions) // Add this line
      })
      .catch((err) => {
        const errorMessage = handleApiError(err, "Failed to load data.")
        showError("Data Load Failed", errorMessage)
      })
  }, [client_id, role_id, token, username, isRoleOne])

  // Add this useEffect after the existing useEffect
  useEffect(() => {
    if (openDropdown && searchInputRef.current) {
      // Auto-focus the search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [openDropdown])

  // Add this useEffect after the existing useEffect for search input focus
  useEffect(() => {
    if (openDropdown && dropdownScrollRef.current && scrollPositions.current[openDropdown]) {
      // Restore scroll position when dropdown reopens
      setTimeout(() => {
        if (dropdownScrollRef.current) {
          dropdownScrollRef.current.scrollTop = scrollPositions.current[openDropdown]
        }
      }, 100)
    }
  }, [openDropdown])

  // Custom dropdown handler
  const toggleDropdown = (field) => {
    if (openDropdown === field) {
      setOpenDropdown(null)
      setIsAnyDropdownOpen(false)
    } else {
      setOpenDropdown(field)
      setIsAnyDropdownOpen(true)
    }
  }

  // Custom Dropdown Component with improved search functionality and FIXED OVERFLOW
  const CustomDropdown = ({ label, field, options }) => {
    // Use cascading options when available, fallback to original options
    const effectiveOptions =
      cascadingOptions[
        field === "companyName"
          ? "company_names"
          : field === "siteCode"
            ? "site_codes"
            : field === "projectName"
              ? "project_names"
              : field === "state"
                ? "states"
                : field === "location"
                  ? "locations"
                  : field === "specificRecord"
                    ? "specific_record"
                    : "company_names"
      ].length > 0
        ? cascadingOptions[
            field === "companyName"
              ? "company_names"
              : field === "siteCode"
                ? "site_codes"
                : field === "projectName"
                  ? "project_names"
                  : field === "state"
                    ? "states"
                    : field === "location"
                      ? "locations"
                      : field === "specificRecord"
                        ? "specific_record"
                        : "company_names"
          ]
        : options

    const searchTerm = searchTerms[field]
    const filteredOptions = searchTerm
      ? effectiveOptions.filter((option) => {
          const searchLower = searchTerm.toLowerCase()
          const optionLower = option.toLowerCase()
          // For project names, also search in the formatted version (with commas)
          if (field === "projectName") {
            const formattedOption = formatProjectNameForDisplay(option).toLowerCase()
            return optionLower.includes(searchLower) || formattedOption.includes(searchLower)
          }
          return optionLower.includes(searchLower)
        })
      : effectiveOptions

    // Define single select fields
    const isSingleSelect = field === "specificRecord" || field === "companyName"

    // Updated to include all fields that should have search (including specificRecord)
    const showSearch = ["siteCode", "companyName", "projectName", "state", "location", "specificRecord"].includes(field)

    // Helper function to get search placeholder text
    const getSearchPlaceholder = (field) => {
      switch (field) {
        case "siteCode":
          return "Search site codes..."
        case "companyName":
          return "Search company names..."
        case "projectName":
          return "Search project names..."
        case "state":
          return "Search states..."
        case "location":
          return "Search locations..."
        case "specificRecord":
          return "Search specific records..."
        default:
          return "Search..."
      }
    }

    return (
      <div className="relative w-full">
        <label className="block mb-1 sm:mb-2 text-xs sm:text-sm md:text-base font-medium text-white">{label}</label>
        {/* FIXED: Improved dropdown container with better overflow handling */}
        <div
          className="flex flex-wrap gap-1 min-h-[32px] sm:min-h-[36px] md:min-h-[42px] lg:min-h-[44px] p-1 sm:p-1.5 md:p-2 rounded-lg sm:rounded-xl border cursor-pointer w-full transition-all duration-200 bg-white/10 border-white/20 hover:bg-white/15 overflow-hidden"
          onClick={() => toggleDropdown(field)}
        >
          {isSingleSelect ? (
            formData[field] ? (
              <div className="flex items-center text-xs rounded-full px-1.5 sm:px-2 py-0.5 sm:py-1 bg-white/20 text-white max-w-full">
                {/* FIXED: Better text truncation with proper max-width and title for tooltip */}
                <span
                  className="mr-1 truncate flex-1 min-w-0"
                  style={{ maxWidth: "calc(100% - 20px)" }}
                  title={formData[field]}
                >
                  {formData[field]}
                </span>
                <X
                  size={10}
                  className="cursor-pointer flex-shrink-0 hover:text-red-400 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 ml-1"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveOption(field, formData[field])
                  }}
                />
              </div>
            ) : (
              <span className="text-xs sm:text-sm md:text-base text-white/60">Select {label}</span>
            )
          ) : formData[field].length > 0 ? (
            formData[field].map((option, idx) => (
              <div
                key={idx}
                className="flex items-center text-xs rounded-full px-1.5 sm:px-2 py-0.5 sm:py-1 bg-white/20 text-white max-w-full"
                style={{ maxWidth: "150px" }} // FIXED: Set explicit max-width for multi-select tags
              >
                {/* FIXED: Better text truncation for multi-select options with project name formatting */}
                <span
                  className="mr-1 truncate flex-1 min-w-0"
                  style={{ maxWidth: "calc(100% - 20px)" }}
                  title={field === "projectName" ? formatProjectNameForDisplay(option) : option}
                >
                  {field === "projectName" ? formatProjectNameForDisplay(option) : option}
                </span>
                <X
                  size={10}
                  className="cursor-pointer flex-shrink-0 hover:text-red-400 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 ml-1"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveOption(field, option)
                  }}
                />
              </div>
            ))
          ) : (
            <span className="text-xs sm:text-sm md:text-base text-white/60">Select {label}</span>
          )}
          <div className="ml-auto flex items-center flex-shrink-0">
            <ChevronDown
              size={12}
              className={`transition-transform text-white/60 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 ${openDropdown === field ? "rotate-180" : ""}`}
            />
          </div>
        </div>

        {openDropdown === field && (
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={(e) => {
                // Don't close if clicking on search input or its container
                if (e.target.closest(".search-container")) {
                  return
                }
                setOpenDropdown(null)
                setIsAnyDropdownOpen(false)
              }}
            />
            <div
              ref={dropdownScrollRef}
              className="absolute mt-1 w-full max-h-32 sm:max-h-40 md:max-h-60 lg:max-h-72 overflow-auto rounded-lg sm:rounded-xl backdrop-blur-md border shadow-lg z-[9999] bg-gray-800 border-white/20"
              onScroll={(e) => {
                // Store scroll position for this field
                scrollPositions.current[field] = e.target.scrollTop
              }}
            >
              {/* Search input for applicable fields */}
              {showSearch && (
                <div className="sticky top-0 bg-gray-900 p-1.5 sm:p-2 border-b border-white/20 search-container">
                  <div className="relative">
                    <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-white/60 h-3 w-3 sm:h-4 sm:w-4" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={(e) => handleSearchTermChange(field, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder={getSearchPlaceholder(field)}
                      className="w-full pl-7 sm:pl-8 md:pl-10 pr-2 sm:pr-3 py-1 sm:py-1.5 md:py-2 bg-white/10 border border-white/20 rounded-md sm:rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      autoComplete="off"
                    />
                  </div>
                </div>
              )}

              {/* Select All option only for multi-select fields */}
              {!isSingleSelect && (
                <div className="sticky top-0 backdrop-blur-md border-b p-1 sm:p-1.5 md:p-2 bg-white/20 border-white/20">
                  <div
                    className="cursor-pointer rounded-md sm:rounded-lg p-1 sm:p-1.5 md:p-2 transition-colors hover:bg-[#d97925] text-white"
                    onClick={() => handleSelectAllDropdown(field)}
                  >
                    <span className="text-xs sm:text-sm">
                      {formData[field].length === effectiveOptions.length ? "Deselect All" : "Select All"}
                    </span>
                  </div>
                </div>
              )}

              {filteredOptions.map((option, idx) => (
                <div
                  key={idx}
                  className={`cursor-pointer p-1 sm:p-1.5 md:p-2 transition-colors ${
                    isSingleSelect
                      ? formData[field] === option
                        ? "bg-white/15"
                        : ""
                      : formData[field].includes(option)
                        ? "bg-white/15"
                        : ""
                  } hover:bg-[#d97925] text-white`}
                  onClick={() => handleOptionSelect(field, option)}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 md:mr-2 rounded border flex-shrink-0 ${
                        isSingleSelect
                          ? formData[field] === option
                            ? "bg-orange-500 border-orange-500"
                            : "border-white/40"
                          : formData[field].includes(option)
                            ? "bg-orange-500 border-orange-500"
                            : "border-white/40"
                      }`}
                    >
                      {((isSingleSelect && formData[field] === option) ||
                        (!isSingleSelect && formData[field].includes(option))) && (
                        <div className="flex items-center justify-center h-full">
                          <svg
                            width="6"
                            height="5"
                            viewBox="0 0 10 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="sm:w-2 sm:h-1.5 md:w-2.5 md:h-2"
                          >
                            <path
                              d="M9 1L3.5 6.5L1 4"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    {/* FIXED: Better text truncation in dropdown options with project name formatting */}
                    <span
                      className="text-xs sm:text-sm truncate"
                      title={field === "projectName" ? formatProjectNameForDisplay(option) : option}
                    >
                      {field === "projectName" ? formatProjectNameForDisplay(option) : option}
                    </span>
                  </div>
                </div>
              ))}

              {showSearch && filteredOptions.length === 0 && (
                <div className="p-2 sm:p-3 md:p-4 text-center text-white/60 text-xs sm:text-sm">
                  No matching {label.toLowerCase()} found
                </div>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  const fromDateOnChange = (newValue) => {
    if (filtersApplied) {
      setFiltersApplied(false)
    }
    setFromMonth(newValue ? dayjs(newValue).startOf("month") : null)
  }

  const toDateOnChange = (newValue) => {
    if (filtersApplied) {
      setFiltersApplied(false)
    }
    setToMonth(newValue ? dayjs(newValue).startOf("month") : null)
  }

  return (
    <div className="min-h-screen">
      <Toast ref={toast} position="top-right" />
      {isLoading && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-md z-50 flex items-center justify-center">
          <ClimbingBoxLoader color="#d97925" size={window.innerWidth < 640 ? 12 : window.innerWidth < 1024 ? 15 : 20} />
        </div>
      )}

      {/* Container 1: Filter Fields */}
      <div className="p-1 sm:p-2 md:p-4 lg:p-6 pb-0">
        <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg p-2 sm:p-3 md:p-4 lg:p-6 w-full max-w-7xl mx-auto text-white relative z-20">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 sm:mb-3 md:mb-4 gap-2">
            <h2 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-semibold">Repository Filters</h2>
          </div>

          {isRoleOne ? (
            // Two-row layout for role 1 (with company name)
            <>
              {/* First row: Company Name, Site Code, Project Name, State */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
                <CustomDropdown label="Company Name" field="companyName" options={dropdownOptions.company_names} />
                <CustomDropdown label="Site Code" field="siteCode" options={dropdownOptions.site_codes} />
                <CustomDropdown label="Project Name" field="projectName" options={dropdownOptions.project_names} />
                <CustomDropdown label="State" field="state" options={dropdownOptions.states} />
              </div>
              {/* Second row: Location, From Date, To Date, Specific Record */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <CustomDropdown label="Location" field="location" options={dropdownOptions.locations} />
                <div className="w-full">
                  <label className="block mb-2 text-sm font-medium text-white">From (MMM/YYYY)</label>
                  <DatePicker
                    views={["year", "month"]}
                    value={fromMonth}
                    onChange={fromDateOnChange}
                    format="MMM/YYYY"
                    slots={{ openPickerIcon: () => <CalendarDays className="text-orange-500" /> }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        InputProps: {
                          style: {
                            color: "#fff",
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderRadius: 12,
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="w-full">
                  <label className="block mb-2 text-sm font-medium text-white">To (MMM/YYYY)</label>
                  <DatePicker
                    views={["year", "month"]}
                    value={toMonth}
                    onChange={toDateOnChange}
                    format="MMM/YYYY"
                    slots={{ openPickerIcon: () => <CalendarDays className="text-orange-500" /> }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        InputProps: {
                          style: {
                            color: "#fff",
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderRadius: 12,
                          },
                        },
                      },
                    }}
                  />
                </div>
                <CustomDropdown
                  label="Specific Record"
                  field="specificRecord"
                  options={dropdownOptions.specific_record}
                />
              </div>
            </>
          ) : (
            // Single row layout for other roles (without company name)
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
                <CustomDropdown label="Site Code" field="siteCode" options={dropdownOptions.site_codes} />
                <CustomDropdown label="Project Name" field="projectName" options={dropdownOptions.project_names} />
                <CustomDropdown label="State" field="state" options={dropdownOptions.states} />
                <CustomDropdown label="Location" field="location" options={dropdownOptions.locations} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
                <div className="w-full">
                  <label className="block mb-2 text-sm font-medium text-white">From (MMM/YYYY)</label>
                  <DatePicker
                    views={["year", "month"]}
                    value={fromMonth}
                    onChange={fromDateOnChange}
                    format="MMM/YYYY"
                    slots={{ openPickerIcon: () => <CalendarDays className="text-orange-500" /> }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        InputProps: {
                          style: {
                            color: "#fff",
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderRadius: 12,
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="w-full">
                  <label className="block mb-2 text-sm font-medium text-white">To (MMM/YYYY)</label>
                  <DatePicker
                    views={["year", "month"]}
                    value={toMonth}
                    onChange={toDateOnChange}
                    format="MMM/YYYY"
                    slots={{ openPickerIcon: () => <CalendarDays className="text-orange-500" /> }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        InputProps: {
                          style: {
                            color: "#fff",
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderRadius: 12,
                          },
                        },
                      },
                    }}
                  />
                </div>
                <CustomDropdown
                  label="Specific Record"
                  field="specificRecord"
                  options={dropdownOptions.specific_record}
                />
              </div>
            </>
          )}

          {/* Apply and Clear buttons with increased margin */}
          <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row gap-0 sm:gap-4 justify-end mt-6">
            <button
              onClick={handleClear}
              className="px-4 sm:px-6 py-2.5 sm:py-2 rounded-full text-white font-medium backdrop-blur-md bg-white/10 border border-white/20 shadow-lg transition hover:bg-white/20 hover:text-red-400 w-full sm:w-auto text-sm sm:text-base"
              disabled={isLoading}
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 sm:px-6 py-2.5 sm:py-2 rounded-full text-white font-medium backdrop-blur-md bg-white/10 border border-white/20 shadow-lg transition hover:bg-white/20 hover:text-[#d97925] w-full sm:w-auto text-sm:text-base"
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Apply"}
            </button>
          </div>
        </div>
      </div>

      {/* Container 2: Table with Pagination */}
      {tableData.length > 0 && (
        <div
          className={`p-1 sm:p-2 md:p-4 lg:p-6 pt-2 sm:pt-3 md:pt-4 transition-all duration-300 ${isAnyDropdownOpen ? "mt-4 sm:mt-8 md:mt-16" : ""}`}
        >
          <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg p-2 sm:p-3 md:p-4 lg:p-6 w-full max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-2 sm:mb-3 md:mb-4 gap-2">
              <h2 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-semibold text-white">
                Repository Data
              </h2>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 md:gap-4 text-xs sm:text-sm">
                <span className="text-white">
                  Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                  {Math.min(currentPage * rowsPerPage, tableData.length)} of {tableData.length} entries
                </span>
              </div>
            </div>

            <div className="w-full">
              <div className="w-full">
                {/* Updated condition: Show Download All button only after filters are applied */}
                {filtersApplied &&
                  (formData.siteCode.length > 0 ||
                    formData.projectName.length > 0 ||
                    formData.state.length > 0 ||
                    formData.location.length > 0) && (
                    <div className="mb-4 flex justify-end">
                      <button
                        onClick={handleDownloadAll}
                        disabled={isLoading || tableData.length === 0 || !formData.specificRecord}
                        className="px-3 sm:px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition flex items-center gap-2 text-sm sm:text-base"
                      >
                        <ZipIcon className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          Download All {formData.specificRecord} ({tableData.length} files)
                        </span>
                        <span className="sm:hidden">Download All ({tableData.length})</span>
                      </button>
                    </div>
                  )}

                <div className="rounded-lg border border-white/10 w-full">
                  <table className="w-full text-white">
                    <thead className=" bg-white/20 backdrop-blur-md border-b border-white/20 z-10">
                      <tr>
                        <th className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs font-semibold text-white text-center">
                          S.No
                        </th>
                        <th className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs font-semibold text-white text-center w-12 sm:w-16 md:w-20 lg:w-24">
                          MMM YYYY
                        </th>
                        {isRoleOne && (
                          <th className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs font-semibold text-white text-left">
                            Company Name
                          </th>
                        )}
                        <th className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs font-semibold text-white text-left">
                          Project Code
                        </th>
                        <th className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs font-semibold text-white text-left">
                          Project Name
                        </th>
                        <th className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-4 text-xs font-semibold text-white text-left">
                          State
                        </th>
                        <th className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs font-semibold text-white text-left">
                          Location
                        </th>
                        <th className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs font-semibold text-white text-center w-10 sm:w-12 md:w-16 lg:w-20">
                          ZIP
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((row, rowIndex) => {
                        const actualRowIndex = (currentPage - 1) * rowsPerPage + rowIndex
                        return (
                          <tr key={actualRowIndex} className="border-b border-white/10 hover:bg-white/5 transition">
                            <td className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs text-center font-medium text-orange-300">
                              {actualRowIndex + 1}
                            </td>
                            <td className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs text-center font-medium text-orange-300">
                              {getRowDate(row)}
                            </td>
                            {isRoleOne && (
                              <td className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs">
                                {getTableFieldValue(row, "company_name") || "-"}
                              </td>
                            )}
                            <td className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs">
                              {getTableFieldValue(row, "project_code") || "-"}
                            </td>
                            <td className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs">
                              {getTableFieldValue(row, "project_name") || "-"}
                            </td>
                            <td className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-4 text-xs">
                              {getTableFieldValue(row, "state") || "-"}
                            </td>
                            <td className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs">
                              {getTableFieldValue(row, "location") || "-"}
                            </td>
                            <td className="px-0.5 sm:px-1 md:px-2 lg:px-4 py-1 sm:py-1.5 md:py-2 lg:py-3 text-xs text-center">
                              <button
                                onClick={() => handleZipDownload(row)}
                                disabled={isLoading}
                                className="p-0.5 sm:p-1 rounded hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Download ZIP file"
                              >
                                <ZipIcon className="w-3 h-3 sm:w-4 sm:h-4 text-orange-400 hover:text-orange-300" />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex flex-col space-y-1.5 sm:space-y-2 md:space-y-0 md:flex-row md:justify-between md:items-center mt-2 sm:mt-3 md:mt-4 lg:mt-6">
              <div className="text-white text-xs sm:text-sm text-center md:text-left order-2 md:order-1">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center justify-center space-x-0.5 sm:space-x-1 order-1 md:order-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="p-0.5 sm:p-1 md:p-2 rounded-md bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 md:py-2 rounded-md text-xs sm:text-sm font-medium transition ${
                      currentPage === page ? "bg-orange-500 text-white" : "bg-white/10 text-white hover:bg-white/20"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="p-0.5 sm:p-1 md:p-2 rounded-md bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>
              <div className="text-white text-xs sm:text-sm text-center md:text-right order-3">
                {tableData.length} total records
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Repository
